import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { ResultadoCalculoConsumo } from '../lib/analiseTecnicaConsumo';

// Mostra, passo a passo e com os números da própria UC, como o consumo médio e o
// kWh compensável foram calculados. Existe porque o analista precisa conferir a
// conta — e porque UC com GD tem regra diferente de UC sem GD, o que era
// invisível quando a tela só cuspia o número final.
//
// Visual: "recibo" — passos numerados à esquerda, valor do passo alinhado à
// direita em tabular-nums, resultado final destacado. Mesma linguagem do resto
// do modal (slate + primary), sem inventar estética nova numa tela de conferência.

interface Props {
    calculo: ResultadoCalculoConsumo;
    className?: string;
}

export default function RegraCalculoConsumo({ calculo, className = '' }: Props) {
    const { passos, possuiGD, injecaoIgnorada, janelaCurta } = calculo;
    const ultimoIdx = passos.length - 1;

    return (
        <div className={`px-5 py-4 bg-slate-50/60 dark:bg-slate-800/25 border-b border-slate-100 dark:border-slate-800 ${className}`}>
            <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">
                    Regra do cálculo
                </span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${possuiGD
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200/80 dark:border-amber-800/50 text-amber-700 dark:text-amber-400'
                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'
                    }`}>
                    {possuiGD ? 'UC com GD' : 'UC sem GD'}
                </span>
                {janelaCurta ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-sky-50 dark:bg-sky-900/20 border-sky-200/80 dark:border-sky-800/50 text-sky-700 dark:text-sky-400">
                        Janela de 3 meses
                    </span>
                ) : null}
            </div>

            <ol className="space-y-0">
                {passos.map((passo, idx) => {
                    const final = idx === ultimoIdx && passos.length > 1;
                    return (
                        <li
                            key={passo.titulo}
                            className={`flex items-start justify-between gap-4 py-2.5 ${idx < ultimoIdx ? 'border-b border-dashed border-slate-200/70 dark:border-slate-700/50' : ''
                                } ${final ? 'mt-1 pt-3 px-3 -mx-3 rounded-lg bg-primary/[0.06] dark:bg-primary/[0.12] border-b-0' : ''}`}
                        >
                            <div className="min-w-0">
                                <p className={`text-xs font-bold ${final ? 'text-primary' : 'text-slate-700 dark:text-slate-200'}`}>
                                    {passo.titulo}
                                </p>
                                <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400 mt-0.5">
                                    {passo.detalhe}
                                </p>
                            </div>
                            <span
                                className={`shrink-0 text-sm font-bold tabular-nums ${final ? 'text-primary' : 'text-slate-600 dark:text-slate-300'}`}
                            >
                                {passo.valor}
                            </span>
                        </li>
                    );
                })}
            </ol>

            {injecaoIgnorada > 0 ? (
                <div className="mt-3 flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200/80 dark:border-amber-800/50">
                    <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
                        Há {injecaoIgnorada.toLocaleString('pt-BR')} kWh de injeção lançados no histórico, mas
                        &ldquo;A Unidade possui GD instalada?&rdquo; não está em <strong>Sim</strong> — essa energia
                        está fora do cálculo. Marque Sim para descontá-la do compensável.
                    </p>
                </div>
            ) : null}
        </div>
    );
}
